import React, { useEffect, useCallback, useRef, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Skeleton } from "@/components/ui/skeleton";
import {
  fetchImageEditHistory,
  magicEditImage,
  fetchImageById,
  stopMagicEdit,
} from "../../apiService";
import { ImageEditHistoryItem } from "../../types";
import { useGlobalStore } from "@/GlobalStore";
import {
  RiAttachmentLine,
  RiBookOpenLine,
  RiCloseLargeLine,
  RiSparkling2Line,
  RiStopCircleLine,
} from "@remixicon/react";
import { cn } from "@/lib/utils";
import {
  MediaLibraryDialog,
  LibraryAttachmentsPreview,
} from "@/components/hoc/mediaLibrary";
import { LibraryMedia } from "@/types";
import {
  FileAttachmentHandler,
  FileAttachmentsPreview,
} from "@/components/hoc/fileAttachment";
import { File as CustomFile } from "@/types";
import { UploadingFile } from "@/components/hoc/fileAttachment/types";
import { uploadFilesAsync } from "@/api/globalApiService";
import { toast } from "sonner";
import ToastContent from "@/components/common/toast/ToastContent";
import { LuInfo, LuTriangleAlert } from "react-icons/lu";

interface MagicEditProps {
  activeImageId?: string;
  currentBackgroundImageEdit?: ImageEditHistoryItem;
  onEdit?: (editResult: ImageEditHistoryItem) => void;
  onImageSelect?: (selectedImage: ImageEditHistoryItem) => void;
  onClose?: () => void;
  onStatusChange?: (imageId: string, status: "pending" | "success" | "failed") => void;
  // State from parent component
  magicEditState?: {
    prompt: string;
    selectedReferenceImageId: string | null;
    pollingImageId: string | null;
    isProcessing: boolean;
    baseImageId: string | null;
    history: ImageEditHistoryItem[];
    isLoadingHistory: boolean;
    isLoadingMore: boolean;
    nextCursor: string | null;
    hasMore: boolean;
  };
  onStateChange?: (
    stateOrUpdater:
      | {
          prompt: string;
          selectedReferenceImageId: string | null;
          pollingImageId: string | null;
          isProcessing: boolean;
          baseImageId: string | null;
          history: ImageEditHistoryItem[];
          isLoadingHistory: boolean;
          isLoadingMore: boolean;
          nextCursor: string | null;
          hasMore: boolean;
        }
      | ((prevState: {
          prompt: string;
          selectedReferenceImageId: string | null;
          pollingImageId: string | null;
          isProcessing: boolean;
          baseImageId: string | null;
          history: ImageEditHistoryItem[];
          isLoadingHistory: boolean;
          isLoadingMore: boolean;
          nextCursor: string | null;
          hasMore: boolean;
        }) => {
          prompt: string;
          selectedReferenceImageId: string | null;
          pollingImageId: string | null;
          isProcessing: boolean;
          baseImageId: string | null;
          history: ImageEditHistoryItem[];
          isLoadingHistory: boolean;
          isLoadingMore: boolean;
          nextCursor: string | null;
          hasMore: boolean;
        }),
  ) => void;
}

const MagicEdit: React.FC<MagicEditProps> = ({
  activeImageId,
  currentBackgroundImageEdit,
  onEdit,
  onImageSelect,
  onClose,
  onStatusChange,
  magicEditState,
  onStateChange,
}) => {
  const { selectedAccount } = useGlobalStore();
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Ref to track if the current magic edit operation should be cancelled
  const cancelledRef = useRef<boolean>(false);
  const currentOperationIdRef = useRef<string | null>(null);

  // Library attachments state
  const [libraryMedia, setLibraryMedia] = useState<LibraryMedia[]>([]);

  // File attachments state
  const [files, setFiles] = useState<CustomFile[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState<UploadingFile[]>([]);
  const [isFileUploading, setIsFileUploading] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Constants
  const ATTACHMENT_LIMIT = 2;
  const ALLOWED_FILE_TYPES = ["image/jpeg", "image/png", "image/webp"] as const;

  // Library attachment handlers
  const handleLibraryAttachmentChange = (attachments: LibraryMedia[]) => {
    setLibraryMedia(attachments);
  };

  const handleRemoveLibraryAttachment = (fileToRemove: {
    id: string;
    public_url: string;
    file_name: string;
  }) => {
    setLibraryMedia((prev) =>
      prev.filter(
        (media) =>
          (media.file_id && media.file_id !== fileToRemove.id) ||
          (!media.file_id && media.file_url !== fileToRemove.public_url),
      ),
    );
  };

  // File attachment handlers
  const handleFileUpload = async (selectedFiles: globalThis.File[]) => {
    // Enforce overall attachment limit
    const capacity = ATTACHMENT_LIMIT - (files.length + libraryMedia.length);
    if (capacity <= 0) {
      toast.message(
        <ToastContent
          icon={LuInfo}
          headingPrefix="Attachment limit reached"
          subheadingPrefix={`You can attach up to ${ATTACHMENT_LIMIT} files in total.`}
          isError={false}
          isWarning={true}
        />,
      );
      return;
    }

    // Trim selected files if they exceed remaining capacity
    const filesToProcess = selectedFiles.slice(0, capacity);

    if (selectedFiles.length > capacity) {
      toast.message(
        <ToastContent
          icon={LuInfo}
          headingPrefix="Some files were not attached"
          subheadingPrefix={`Only ${ATTACHMENT_LIMIT} attachments allowed.`}
          isError={false}
          isWarning={true}
        />,
      );
    }

    if (filesToProcess.length > 0) {
      // Check if any file exceeds 100MB
      const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB in bytes
      const oversizedFiles = filesToProcess.filter((file) => file.size > MAX_FILE_SIZE);

      if (oversizedFiles.length > 0) {
        toast.message(
          <ToastContent
            icon={LuTriangleAlert}
            headingPrefix="File size too large"
            subheadingPrefix="Files must be under 100MB. Please select smaller files."
            isError={true}
          />,
        );
        return;
      }

      // Validate file types
      const invalidFiles = filesToProcess.filter(
        (file) =>
          !ALLOWED_FILE_TYPES.includes(file.type as (typeof ALLOWED_FILE_TYPES)[number]),
      );

      if (invalidFiles.length > 0) {
        toast.message(
          <ToastContent
            icon={LuTriangleAlert}
            headingPrefix="Invalid file type"
            subheadingPrefix={`Only ${ALLOWED_FILE_TYPES.map((type) => type.split("/")[1].toUpperCase()).join(", ")} files are allowed.`}
            isError={true}
          />,
        );
        return;
      }

      // Create immediate preview items with temp IDs
      const newUploadingItems = filesToProcess.map((file) => {
        const tempId = `temp-${Date.now()}-${Math.random()}`;
        const preview = URL.createObjectURL(file);
        return { file, preview, id: tempId };
      });
      setUploadingFiles(newUploadingItems);

      setIsFileUploading(true);
      try {
        const response = await uploadFilesAsync(
          filesToProcess,
          selectedAccount?.id || "",
        );
        setFiles((prev) => {
          // Use a Map to deduplicate by id, always keeping the first occurrence
          const filesById = new Map<string, CustomFile>();
          [...prev, ...response.files].forEach((file) => {
            if (!filesById.has(file.id)) {
              filesById.set(file.id, file);
            }
          });
          return Array.from(filesById.values());
        });
        setUploadingFiles([]);
        // Clean up object URLs to prevent memory leaks after upload
        newUploadingItems.forEach((item) => {
          if (item.preview) {
            URL.revokeObjectURL(item.preview);
          }
        });
      } catch (error) {
        console.error("Error uploading files:", error);
        // Clean up uploading files and object URLs on error
        setUploadingFiles([]);
        newUploadingItems.forEach((item) => {
          if (item.preview) {
            URL.revokeObjectURL(item.preview);
          }
        });
      } finally {
        setIsFileUploading(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const selectedFiles = Array.from(event.target.files);
      void handleFileUpload(selectedFiles);
    }
  };

  const handleRemoveFile = (fileToRemove: CustomFile) => {
    setFiles((prev) =>
      prev.filter(
        (file) =>
          (file.id && file.id !== fileToRemove.id) ||
          (!file.id && file.public_url !== fileToRemove.public_url),
      ),
    );
  };
  // Use state from parent component or initialize defaults
  const magicEditPrompt = magicEditState?.prompt || "";
  const selectedReferenceImageId = magicEditState?.selectedReferenceImageId || null;
  const pollingImageId = magicEditState?.pollingImageId || null;
  const isProcessingMagicEdit = magicEditState?.isProcessing || false;
  const editHistory = useMemo(
    () => magicEditState?.history || [],
    [magicEditState?.history],
  );
  const isLoadingEditHistory = magicEditState?.isLoadingHistory || false;
  const isLoadingMore = magicEditState?.isLoadingMore || false;
  const nextCursor = magicEditState?.nextCursor || null;
  const hasMore = magicEditState?.hasMore || false;

  // Helper function to update state in parent
  const updateState = useCallback(
    (
      updates: Partial<{
        prompt: string;
        selectedReferenceImageId: string | null;
        pollingImageId: string | null;
        isProcessing: boolean;
        baseImageId: string | null;
        history: ImageEditHistoryItem[];
        isLoadingHistory: boolean;
        isLoadingMore: boolean;
        nextCursor: string | null;
        hasMore: boolean;
      }>,
    ) => {
      if (onStateChange) {
        onStateChange((prevState) => ({
          ...prevState,
          ...updates,
        }));
      }
    },
    [onStateChange],
  );

  // Helper function to pre-load an image
  const preloadImage = (url: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve();
      img.onerror = () => reject(new Error(`Failed to load image: ${url}`));
      img.src = url;
    });
  };

  // Polling for magic edit completion
  const pollImageStatus = useCallback(
    async (
      imageId: string,
      operationId: string,
    ): Promise<ImageEditHistoryItem | null> => {
      const accountId = selectedAccount?.id;
      if (!accountId) return null;

      // Check if this operation was cancelled
      if (cancelledRef.current || currentOperationIdRef.current !== operationId) {
        console.log("Polling cancelled for operation:", operationId);
        return null;
      }

      try {
        const response = await fetchImageById(String(accountId), imageId);

        // Check again after async operation
        if (cancelledRef.current || currentOperationIdRef.current !== operationId) {
          console.log("Polling cancelled for operation:", operationId);
          return null;
        }

        if (response.status === "success") {
          const imageData = response.data;
          if (imageData.status === "success") {
            return imageData;
          } else if (imageData.status === "pending") {
            // Continue polling after a delay
            await new Promise((resolve) => setTimeout(resolve, 2000));

            // Check again after delay
            if (cancelledRef.current || currentOperationIdRef.current !== operationId) {
              console.log("Polling cancelled for operation:", operationId);
              return null;
            }

            return pollImageStatus(imageId, operationId);
          } else {
            // Failed status
            throw new Error(imageData.error_message || "Magic edit failed");
          }
        }
        throw new Error("Failed to fetch image status");
      } catch (error) {
        console.error("Error polling image status:", error);
        throw error;
      }
    },
    [selectedAccount?.id],
  );

  // Fetch edit history for the current image
  const fetchEditHistoryForImage = useCallback(
    async (imageId: string) => {
      const accountId = selectedAccount?.id;
      if (!accountId || !imageId) return;

      // Skip fetching history for temporary image IDs
      if (String(imageId).startsWith("temp-")) {
        updateState({
          history: [],
          isLoadingHistory: false,
          nextCursor: null,
          hasMore: false,
        });
        return;
      }

      if (!editHistory.length) updateState({ isLoadingHistory: true });
      try {
        const response = await fetchImageEditHistory(String(accountId), imageId, 20);
        if (response.status === "success") {
          const historyItems = response.data.edits || [];
          updateState({
            history: historyItems,
            nextCursor: response.pagination.next_cursor,
            hasMore: response.pagination.has_more,
          });

          // Auto-select the image that matches the current visible image URL
          if (currentBackgroundImageEdit && historyItems.length > 0) {
            const matchingItem = historyItems.find(
              (item) => item.id === currentBackgroundImageEdit.id,
            );
            if (matchingItem) {
              updateState({ selectedReferenceImageId: matchingItem.id });
            } else {
              // If no exact match, select the most recent (first) item
              updateState({ selectedReferenceImageId: historyItems[0].id });
            }
          } else if (historyItems.length > 0) {
            // Auto-select the most recent image as reference if none selected
            updateState({ selectedReferenceImageId: historyItems[0].id });
          }
        }
      } catch (error) {
        console.error("Failed to fetch edit history:", error);
        updateState({ history: [], nextCursor: null, hasMore: false });
      } finally {
        updateState({ isLoadingHistory: false });
      }
    },
    [selectedAccount?.id, currentBackgroundImageEdit, updateState, editHistory.length],
  );

  // Load more edit history items
  const loadMoreHistory = useCallback(async () => {
    const accountId = selectedAccount?.id;
    if (!accountId || !activeImageId || !nextCursor || isLoadingMore || !hasMore) return;

    updateState({ isLoadingMore: true });
    try {
      const response = await fetchImageEditHistory(
        String(accountId),
        activeImageId,
        20,
        nextCursor,
      );
      if (response.status === "success") {
        const newHistoryItems = response.data.edits || [];
        updateState({
          history: [...editHistory, ...newHistoryItems],
          nextCursor: response.pagination.next_cursor,
          hasMore: response.pagination.has_more,
        });
      }
    } catch (error) {
      console.error("Failed to load more edit history:", error);
    } finally {
      updateState({ isLoadingMore: false });
    }
  }, [
    selectedAccount?.id,
    activeImageId,
    nextCursor,
    isLoadingMore,
    hasMore,
    editHistory,
    updateState,
  ]);

  // Handle scroll to load more items
  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
      const isNearBottom = scrollTop + clientHeight >= scrollHeight - 100; // Load when 100px from bottom

      if (isNearBottom && hasMore && !isLoadingMore && !isLoadingEditHistory) {
        void loadMoreHistory();
      }
    },
    [hasMore, isLoadingMore, isLoadingEditHistory, loadMoreHistory],
  );

  // Handle active image changes
  useEffect(() => {
    if (activeImageId) {
      const selectedItem = editHistory.find(
        (item) => item.id === selectedReferenceImageId,
      );
      if (selectedItem) {
        onImageSelect?.(selectedItem);
      }
      // Fetch history for new image
      void fetchEditHistoryForImage(activeImageId);
    } else {
      // Clear history state when no active image
      updateState({
        history: [],
        isLoadingHistory: false,
        isLoadingMore: false,
        nextCursor: null,
        hasMore: false,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeImageId]);

  const handleStopEdit = async () => {
    if (!pollingImageId) return;

    const accountId = selectedAccount?.id;
    if (!accountId) return;

    // Immediately set cancellation flags to stop ongoing operations
    cancelledRef.current = true;
    currentOperationIdRef.current = null;

    try {
      updateState({
        pollingImageId: null,
        isProcessing: false,
        prompt: magicEditPrompt, // Explicitly preserve the current prompt
      });
      await stopMagicEdit(String(accountId), pollingImageId);
      // Preserve the current prompt when stopping

      // Reset the media status when magic edit is cancelled
      if (onStatusChange && activeImageId) {
        onStatusChange(activeImageId, "success");
      }
      // Refresh edit history to get updated status
      if (activeImageId) {
        void fetchEditHistoryForImage(activeImageId);
      }
    } catch (error) {
      console.error("Failed to stop magic edit:", error);
    }
  };

  const handleEdit = async () => {
    if (!magicEditPrompt.trim() || !selectedReferenceImageId || !activeImageId) return;

    const accountId = selectedAccount?.id;
    if (!accountId) return;

    // Generate a unique operation ID for this edit operation
    const operationId = `edit-${Date.now()}-${Math.random()}`;
    currentOperationIdRef.current = operationId;
    cancelledRef.current = false;

    updateState({ isProcessing: true });

    // Mark the media as pending when magic edit starts
    if (onStatusChange) {
      onStatusChange(activeImageId, "pending");
    }

    try {
      // Call magic edit API
      const response = await magicEditImage(String(accountId), activeImageId, {
        reference_image_edit_id: selectedReferenceImageId,
        prompt: magicEditPrompt,
        file_ids: files.map((f) => f.id),
        library_file_ids: libraryMedia.map((m) => m.file_id),
      });

      // Check if operation was cancelled after API call
      if (cancelledRef.current || currentOperationIdRef.current !== operationId) {
        console.log("Operation cancelled after API call:", operationId);
        return;
      }

      if (response.status === "success") {
        const editResult = response.data;

        if (editResult.status === "pending") {
          // Start polling for completion
          updateState({ pollingImageId: editResult.id, isProcessing: true });
          try {
            const completedResult = await pollImageStatus(editResult.id, operationId);

            // Check if operation was cancelled during polling
            if (cancelledRef.current || currentOperationIdRef.current !== operationId) {
              console.log("Operation cancelled during polling:", operationId);
              return;
            }

            if (completedResult && onEdit) {
              // Pre-load the image before calling onEdit
              if (completedResult.permalink_url) {
                try {
                  await preloadImage(completedResult.permalink_url);
                } catch (error) {
                  console.warn("Failed to pre-load completed image:", error);
                }
              }

              // Final check before applying changes
              if (cancelledRef.current || currentOperationIdRef.current !== operationId) {
                console.log("Operation cancelled before applying changes:", operationId);
                return;
              }

              onEdit(completedResult);
              // Mark the media as success when magic edit completes
              if (onStatusChange) {
                onStatusChange(activeImageId, "success");
              }
              // Add the completed result to the correct base image's history
              const baseImageId = magicEditState?.baseImageId || activeImageId;
              if (baseImageId) {
                // Add to the base image's history
                updateState({
                  history: [completedResult, ...editHistory],
                  selectedReferenceImageId: completedResult.id,
                });
              }
              // Clear prompt only after successful generation
              updateState({ prompt: "" });
            }
          } catch (pollError) {
            console.error("Error during polling:", pollError);
            // Don't update state if cancelled
            if (!cancelledRef.current && currentOperationIdRef.current === operationId) {
              // Mark as failed only if not cancelled
              if (onStatusChange && activeImageId) {
                onStatusChange(activeImageId, "failed");
              }
            }
          }
        } else if (editResult.status === "success") {
          // Check if operation was cancelled before processing immediate success
          if (cancelledRef.current || currentOperationIdRef.current !== operationId) {
            console.log("Operation cancelled before processing success:", operationId);
            return;
          }

          // Immediately completed
          if (onEdit) {
            // Pre-load the image before calling onEdit
            if (editResult.permalink_url) {
              try {
                await preloadImage(editResult.permalink_url);
              } catch (error) {
                console.warn("Failed to pre-load completed image:", error);
              }
            }

            // Final check before applying changes
            if (cancelledRef.current || currentOperationIdRef.current !== operationId) {
              console.log(
                "Operation cancelled before applying immediate success:",
                operationId,
              );
              return;
            }

            onEdit(editResult);
            // Mark the media as success when magic edit completes
            if (onStatusChange) {
              onStatusChange(activeImageId, "success");
            }
            // Add the completed result to the correct base image's history
            const baseImageId = magicEditState?.baseImageId || activeImageId;
            if (baseImageId) {
              // Add to the base image's history
              updateState({
                history: [editResult, ...editHistory],
                selectedReferenceImageId: editResult.id,
              });
            }
            // Clear prompt only after successful generation
            updateState({ prompt: "" });
          }
        }
      }
    } catch (error) {
      console.error("Magic edit failed:", error);

      // Mark the media as failed when magic edit fails (only if not cancelled)
      if (!cancelledRef.current && currentOperationIdRef.current === operationId) {
        if (onStatusChange && activeImageId) {
          onStatusChange(activeImageId, "failed");
        }
      }
    } finally {
      // Only update state if this operation is still current
      if (!cancelledRef.current && currentOperationIdRef.current === operationId) {
        updateState({ pollingImageId: null, isProcessing: false });
      }
    }
  };

  return (
    <TooltipProvider>
      <div className="flex flex-col h-full">
        <div className="flex-shrink-0">
          <div className="mb-4">
            <div className="flex items-center justify-between px-[18px] py-[12px] border-b">
              <label
                className="text-sm font-semibold text-zinc-950 tracking-wide leading-[19.157px]"
                style={{ fontFamily: "Inter", letterSpacing: "0.28px" }}
              >
                Magic Edit
              </label>
              {onClose && (
                <button onClick={onClose} aria-label="Close Magic Edit">
                  <RiCloseLargeLine size={14} className="text-black" />
                </button>
              )}
            </div>
            <div className="px-4 py-[15px] flex flex-col gap-2">
              {/* Combined attachments and textarea with single border */}
              <div className="border border-[#E7E8E9] rounded-lg bg-white shadow-sm px-[14px] py-[12px]">
                {/* Attachments preview */}
                {([...uploadingFiles, ...files].length > 0 ||
                  libraryMedia.length > 0) && (
                  <div className="flex flex-wrap items-center gap-2 mb-[6px]">
                    {[...uploadingFiles, ...files].length > 0 && (
                      <FileAttachmentsPreview
                        files={files}
                        onRemove={handleRemoveFile}
                        uploadingFiles={uploadingFiles}
                        className="!overflow-visible py-0 flex-none"
                      />
                    )}
                    {libraryMedia.length > 0 && (
                      <LibraryAttachmentsPreview
                        attachments={libraryMedia.map((m) => ({
                          id: m.file_id,
                          public_url: m.file_url,
                          file_name: m.file_name,
                        }))}
                        onRemove={handleRemoveLibraryAttachment}
                        className="!overflow-visible py-0 flex-none"
                      />
                    )}
                  </div>
                )}

                {/* Textarea without individual border */}
                <textarea
                  value={magicEditPrompt}
                  onChange={(e) => updateState({ prompt: e.target.value })}
                  onKeyDown={(e) => {
                    e.stopPropagation();
                    if (e.key === "Enter" && !e.shiftKey && !isProcessingMagicEdit) {
                      e.preventDefault();
                      if (magicEditPrompt.trim() && selectedReferenceImageId) {
                        void handleEdit();
                      }
                    }
                  }}
                  placeholder="Describe your edit"
                  className="w-full h-[216px] text-[12px] text-brand-primary-800 placeholder:text-brand-primary-400 bg-transparent resize-none text-sm border-0 focus:outline-none"
                  disabled={isProcessingMagicEdit}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1">
                  <FileAttachmentHandler
                    acceptableFileTypes={ALLOWED_FILE_TYPES.join(",")}
                    multiple={true}
                    isFileUploading={isFileUploading}
                    fileInputRef={fileInputRef}
                    onFileChange={handleFileChange}
                    disabled={
                      isFileUploading ||
                      files.length + libraryMedia.length >= ATTACHMENT_LIMIT
                    }
                    renderTrigger={({ onClick, disabled }) => (
                      <Button size={"icon"} onClick={onClick} disabled={disabled}>
                        <RiAttachmentLine
                          size={14}
                          className="shrink-0 text-brand-primary-400"
                        />
                      </Button>
                    )}
                  />
                  <MediaLibraryDialog
                    onAttachmentChange={handleLibraryAttachmentChange}
                    attachLimit={ATTACHMENT_LIMIT - files.length}
                    limitMessage={`You can only attach up to ${ATTACHMENT_LIMIT} files in total.`}
                    attachments={libraryMedia}
                    trigger={
                      <Button size={"icon"}>
                        <RiBookOpenLine
                          size={14}
                          className="shrink-0 text-brand-primary-400"
                        />
                      </Button>
                    }
                  />
                </div>
                <div>
                  <Button
                    size={"default"}
                    className={cn(
                      "w-[79px] rounded-lg shadow-sm",
                      isProcessingMagicEdit
                        ? "bg-white text-brand-primary-800"
                        : "border-none bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 text-white ",
                    )}
                    disabled={!magicEditPrompt.trim() || !selectedReferenceImageId}
                    onClick={() =>
                      isProcessingMagicEdit ? void handleStopEdit() : void handleEdit()
                    }
                  >
                    {isProcessingMagicEdit ? (
                      <RiStopCircleLine
                        size={14}
                        className="shrink-0 text-brand-primary-800"
                      />
                    ) : (
                      <RiSparkling2Line size={14} className="shrink-0 text-white" />
                    )}
                    <span className="text-[12px] font-medium mt-[2px]">
                      {isProcessingMagicEdit ? "Stop" : "Edit"}
                    </span>{" "}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Edit History Section */}
        <div className="flex-1 overflow-hidden">
          <div
            ref={scrollContainerRef}
            className="h-full px-4 pb-4 overflow-auto"
            onScroll={handleScroll}
          >
            {isLoadingEditHistory ? (
              <div className="space-y-[6px]">
                {Array.from({ length: 5 }).map((_, index) => (
                  <div
                    key={index}
                    className="flex h-10 py-2.5 px-3 pl-1.5 items-center gap-2.5"
                  >
                    <Skeleton className="w-7 h-7 rounded-[4px]" />
                    <Skeleton className="flex-1 h-3 rounded" />
                  </div>
                ))}
              </div>
            ) : editHistory.length === 0 ? (
              <div className="text-center py-8 text-gray-500 text-sm">
                No edit history found
              </div>
            ) : (
              <div className="space-y-[6px]">
                {editHistory.map((edit) => (
                  <Tooltip key={edit.id}>
                    <TooltipTrigger asChild>
                      <div
                        className={`border cursor-pointer transition-colors flex h-10 py-2.5 px-3 pl-1.5 items-center gap-2.5 border-none rounded-[8px] squircle squircle-radius-[8px] squircle-outline-[1px]  ${
                          selectedReferenceImageId === edit.id
                            ? "bg-brand-orange/5 squircle-outline-color-brand-orange"
                            : "bg-white squircle-outline-color-border-gray "
                        } ${isProcessingMagicEdit ? "opacity-50 pointer-events-none" : ""}`}
                        onClick={() => {
                          if (isProcessingMagicEdit) return;
                          updateState({ selectedReferenceImageId: edit.id });
                          // Also trigger image selection for preview
                          if (onImageSelect && edit.permalink_url) {
                            onImageSelect(edit);
                          }
                        }}
                      >
                        <div className="flex items-center gap-2.5 flex-1">
                          {edit.permalink_url && (
                            <div
                              className="rounded-[4px] overflow-hidden flex-shrink-0 bg-gray-100"
                              style={{
                                width: "28px",
                                height: "28px",
                                aspectRatio: "1/1",
                              }}
                            >
                              <img
                                src={edit.permalink_url}
                                alt="Edit result"
                                className="w-full h-full object-cover"
                                crossOrigin="anonymous"
                              />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="overflow-hidden text-ellipsis text-[12px] text-brand-primary-800 font-normal leading-[19.157px] tracking-[0.24px] line-clamp-1">
                              {edit.prompt}
                            </p>
                          </div>
                        </div>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="right" align="start">
                      <p>{edit.prompt}</p>
                    </TooltipContent>
                  </Tooltip>
                ))}

                {/* Loading more indicator */}
                {isLoadingMore && (
                  <div className="flex items-center justify-center py-4">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
};

export default MagicEdit;
